import { faChevronLeft, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DOMPurify from 'dompurify';
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LanguageKeys } from "../../contexts/LanguageKeys";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import HelperTopics from "../../helperTopics.json";
import { useGlobalState } from "../../Providers";
import "./help-topics-modal.css";

const HelpTopicModal = ({ setShowHelpModal }) => {
    const path = useSelector((state: any) => (state.router.location.pathname));
    const [contentBody, setContentBody] = useState('');
    const [contentTile, setContentTitle] = useState('');
    const [currentTopics, setCurrentTopics] = useState([]) as any;
    const { locale, translate } = useLocale();
    const [generalTopics, setGeneralTopics] = useState([]) as any;
    const { specificHelpTopicKey } = useGlobalState() ?? { setSpecificHelpTopicKey: undefined };
    const HELP_DIR = {
        CONTEXTUAL: 'ContextualHelp',
        GENERAL: 'General'
    };

    useEffect(() => {
        // Contextual help topics
        const splitPath = path.split('/');
        const getActiveTopics = (contextualPath: string) => {
            const contextual = HelperTopics.Contextual.find(item => item.Path === contextualPath) as any;
            return contextual?.Topics?.filter(item => item.Active === true) as any;
        };

        if (specificHelpTopicKey) {
            const segmentPrefix = splitPath[3] ?? splitPath[2];
            // Try the segment-specific path first (e.g. lock-preset/LOCK_PRESET or reports/ALL_REPORTS).
            // For report pages reached via a subcategory, the URL segment is plural (e.g. transaction-reports)
            // but all per-report help entries are stored under the singular "report/" prefix, so fall back
            // to that only when we're inside the reports section.
            const specificTopics = getActiveTopics(segmentPrefix + '/' + specificHelpTopicKey)
                ?? (splitPath[2] === 'reports' ? getActiveTopics('report/' + specificHelpTopicKey) : undefined);
            if (specificTopics?.length) {
                setCurrentTopics(specificTopics);
            } else {
                const contextualTopics = splitPath[3] ? getActiveTopics(splitPath[3]) : getActiveTopics(splitPath[2]);
                setCurrentTopics(contextualTopics ? contextualTopics : []);
            }
        }
        else if (splitPath[3]) {
            const contextualTopics = getActiveTopics(splitPath[3]);
            if (contextualTopics?.length) {
                setCurrentTopics(contextualTopics);
            } else {
                setCurrentTopics([]);
            }
        }
        else if (splitPath.length === 3) {
            if (splitPath[2] === 'enrolment' || splitPath[2] === 'hardware' || splitPath[2] === 'reports' || splitPath[2] === 'notifications' || splitPath[2] === 'lifts') {
                const contextualTopics = getActiveTopics(splitPath[2]);
                setCurrentTopics(contextualTopics ? contextualTopics : []);
            } else {
                setCurrentTopics([]);
            }
        }
        setContentBody('');
        setContentTitle('');

        // General help topics
        const filteredGeneral = HelperTopics.General.filter(item => item.Active === true) as any;
        setGeneralTopics(filteredGeneral);

    }, [path, specificHelpTopicKey]);

    const defaultToEnglish = async (item, dirName): Promise<void> => {
        try {
            const englishHtml = await fetch(`/helpFiles/${dirName}/${LanguageKeys.english}/${item.File}.html`).then(res => res.text());
            const cleanHtml = DOMPurify.sanitize(englishHtml);

            if (cleanHtml) {
                setContentBody(cleanHtml);
                setContentTitle(item.Title);
                return;
            }

            setContentTitle(translate.byKey(TranslationKeys.notFound));
            setContentBody(translate.byKey(TranslationKeys.helpTopicTopicNotFoundMessage))
        } catch (error) {
            setContentTitle(translate.byKey(TranslationKeys.notFound));
            setContentBody(translate.byKey(TranslationKeys.helpTopicTopicNotFoundMessage));
        }
    }

    const loadTopic = async (topic, dirName: string): Promise<void> => {
        try {
            const html = await fetch(`/helpFiles/${dirName}/${locale}/${topic.File}.html`).then(res => res.text());
            const cleanHtml = DOMPurify.sanitize(html);

            if (cleanHtml) {
                setContentBody(cleanHtml);
                setContentTitle(translate.byKey(topic.titleTranslationKey));
                return;
            }

            defaultToEnglish(topic, dirName);
        } catch (error) {
            defaultToEnglish(topic, dirName);
        }
    }

    const handleOnBack = () => {
        setContentBody('');
        setContentTitle('');
    }

    const listComponent = (topics: any[], listTitle: string, dirName: string) => {
        return <div className="mt-2 sm:mt-2 mx-4" >
            <ul className="divide-y divide-gray-200 w-full " >
                {topics.length !== 0 && <li className="py-1 flex">
                    <span className="ml-1 text-sm text-gray-900 font-bold">{listTitle}</span>
                </li>}
                {topics.map((topic, index) =>
                    <li key={`${topic.Title}${index}`} className="py-1 flex ml-3 text-sm hover:text-portal-blue-primary font-medium cursor-pointer" onClick={() => loadTopic(topic, dirName)}>
                        {translate.byKey(topic.titleTranslationKey)}
                    </li>
                )}
            </ul>
        </div>
    }

    return <div className="help-modal position-float-modal flex flex-col" style={{ marginTop: "30px", width: "400px" }} id='helpTopicsModal'>
        <div className="bg-white border-b border-gray-200 sm:px-1">
            <div className=" -mt-4  flex justify-between items-start flex-wrap sm:flex-nowrap">
                <div className=" mt-2 mb-2">
                    <h3 className="text-lg leading-6 text-gray-800 font-bold">
                        {contentBody && <FontAwesomeIcon onClick={() => handleOnBack()} className="mb-1 text-portal-blue-primary h-4 text-5xl cursor-pointer" icon={faChevronLeft} />}
                        <span className={`${!contentBody && 'ml-4'}`} >
                            {contentTile ? contentTile : translate.byKey(TranslationKeys.helpTopics)}
                        </span>
                    </h3>
                </div>
                <div className="ml-4 mt-2 flex-shrink-0">
                    <FontAwesomeIcon onClick={() => setShowHelpModal(false)} className="pr-1 text-red-500 h-4 text-3xl cursor-pointer" icon={faTimes} />
                </div>
            </div>
        </div>
        {contentBody ?
            <div className="overflow-y-auto scrollbar sm:mt-4 mx-4 text-sm font-sans p-3 " style={{ maxHeight: '500px', marginBottom: '16px', marginTop: '6px' }}>
                <div className="help-topic-content mb-4" dangerouslySetInnerHTML={{ __html: contentBody }}>
                </div>
            </div> : <>
                <div className='overflow-y-auto scrollbar' style={{ maxHeight: '500px', marginBottom: '16px', marginTop: '6px' }}>
                    {listComponent(currentTopics, translate.byKey(TranslationKeys.howTo), HELP_DIR.CONTEXTUAL)}
                    {listComponent(generalTopics, translate.byKey(TranslationKeys.general), HELP_DIR.GENERAL)}
                </div>
            </>
        }
    </div>
}

export default HelpTopicModal;
